import { apiClient } from "@/services/api/axiosClient";
import type { ApiResponse } from "@/services/api/api.types";

/* -------------------------------------------------------------------------- */
/*                                    TYPES                                   */
/* -------------------------------------------------------------------------- */

export type SubmitSignaturePayload = {
  property_type: string;
  property_id: string;
  signature: string;
};

export type SubmitSignatureResponse = {
  status: string;
  message: string;
  data?: unknown;
};

export type UploadKYCFilePayload = {
  property_id: string;
  document_type: string;
  /** Browser `File`, or React Native-style descriptor for native clients */
  file: File | { uri: string; type: string; name: string };
};

export type SubmitKYCDataPayload = {
  property_id: string;
  driving_licence_url: string;
};

export type SubmitKYCDataResponse = {
  status: string;
  message: string;
  data?: unknown;
};

export type UploadKYCFileResponse = {
  url?: string;
  driving_licence_url?: string;
};

export type VerifyLastNamePayload = {
  property_id: string;
  last_name: string;
};

export type VerifyLastNameResponse = {
  is_valid: boolean;
  message: string;
  matched_owners: unknown[] | null;
};

/* -------------------------------------------------------------------------- */
/*                                  SERVICES                                  */
/* -------------------------------------------------------------------------- */

export const signatureService = {
  /**
   * Submit signature for property protest documents
   */
  async submitSignature(
    payload: SubmitSignaturePayload
  ): Promise<ApiResponse<SubmitSignatureResponse>> {
    const { data } = await apiClient.post<ApiResponse<SubmitSignatureResponse>>(
      `/api/property-service/documents/signed`,
      payload
    );
    return data;
  },

  /**
   * Upload KYC file (driving license, formation document, etc.)
   * Uses FormData for file upload
   */
  async uploadKYCFile(payload: UploadKYCFilePayload): Promise<ApiResponse<UploadKYCFileResponse>> {
    const formData = new FormData();
    formData.append("property_id", payload.property_id);
    formData.append("document_type", payload.document_type);
    if (payload.file instanceof File) {
      formData.append("file", payload.file);
    } else {
      formData.append("file", {
        uri: payload.file.uri,
        type: payload.file.type,
        name: payload.file.name,
      } as unknown as Blob);
    }

    const { data } = await apiClient.post<ApiResponse<UploadKYCFileResponse>>(
      `/api/property-service/kyc/property-protest/upload`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    );
    return data;
  },

  /**
   * Submit KYC data with driving license URL
   */
  async submitKYCData(payload: SubmitKYCDataPayload): Promise<ApiResponse<SubmitKYCDataResponse>> {
    const { data } = await apiClient.post<ApiResponse<SubmitKYCDataResponse>>(
      `/api/property-service/kyc/property-protest`,
      payload
    );
    return data;
  },

  /**
   * Verify last name against property owners
   */
  async verifyLastName(
    payload: VerifyLastNamePayload
  ): Promise<ApiResponse<VerifyLastNameResponse>> {
    const { data } = await apiClient.post<ApiResponse<VerifyLastNameResponse>>(
      `/api/user-service/validate/owner/lastname`,
      payload
    );
    return data;
  },
};
